import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Bot, Play, Clock, CheckCircle, XCircle, AlertCircle, RefreshCw, AlertTriangle, Search } from 'lucide-react';

interface AgentExecution {
  id: string;
  tool_name: string;
  parameters: any;
  result: any;
  status: string;
  started_at: string;
  completed_at?: string;
  execution_time_ms?: number;
  error_message?: string;
}

interface NeoExecution {
  run_id: string;
  dataset: string;
  status: string;
  rows_inserted: number;
  rows_updated?: number;
  duration_ms: number;
  provider: string;
  fallback_used: boolean;
  created_at: string;
  error_message?: string;
  result?: {
    metrics?: {
      last_error?: string;
    };
  };
}

const TASK_TYPES = [
  { value: 'social', label: 'Social History', description: 'Twitter, Reddit, engagement data' },
  { value: 'derivatives', label: 'Derivatives', description: 'BTC/ETH funding, open interest' },
  { value: 'whales', label: 'Whales Activity', description: 'On-chain whale movements' },
  { value: 'fng', label: 'Fear & Greed', description: 'Market sentiment index' },
];

const BACKFILL_PERIODS = [
  { value: 1, label: '24 Hours' },
  { value: 7, label: '7 Days' },
  { value: 30, label: '30 Days' },
  { value: 90, label: '90 Days' },
];

export default function NeoAgentPanel() {
  const [executions, setExecutions] = useState<NeoExecution[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadNeoExecutions();
    const interval = setInterval(loadNeoExecutions, 10000); // Refresh every 10s
    return () => clearInterval(interval);
  }, []);

  const loadNeoExecutions = async () => {
    try {
      const { data, error } = await supabase
        .from('agent_executions_public')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      setExecutions(data || []);
    } catch (error) {
      console.error('Error loading Neo executions:', error);
    }
  };

  const startNeoTask = async (taskType: string, days?: number, preferProvider?: string, allowFallback?: boolean, slugs?: string[]) => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      const params = {
        task_type: taskType,
        days: days || 1,
        symbols: taskType === 'derivatives' ? ['BTC', 'ETH'] : undefined,
        slugs: slugs || (taskType === 'social' ? ['bitcoin', 'ethereum'] : undefined),
        prefer_provider: preferProvider,
        allow_fallback: allowFallback,
      };

      const { data, error } = await supabase.functions.invoke('flowith-neo', {
        body: {
          action: 'start',
          task_type: taskType,
          params
        }
      });

      if (error) throw error;

      if (data.success) {
        const providerSuffix = preferProvider === 'neo' ? ' (Neo only)' : '';
        toast({
          title: "Neo Task Started",
          description: `${TASK_TYPES.find(t => t.value === taskType)?.label} collection initiated${days ? ` (${days}d backfill)` : ''}${providerSuffix}`,
        });
        
        // Refresh executions after a short delay
        setTimeout(loadNeoExecutions, 2000);
      } else {
        throw new Error(data.error || 'Failed to start Neo task');
      }

    } catch (error) {
      console.error('Error starting Neo task:', error);
      
      const errorMessage = error.message || 'Unknown error';
      const isNon2xx = errorMessage.includes('Edge Function returned a non-2xx status code');
      
      toast({
        title: "Error",
        description: isNon2xx 
          ? `Neo service unavailable. ${errorMessage}` 
          : `Failed to start ${taskType} collection: ${errorMessage}`,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRetryWithFallback = async (executionId: string, params: any) => {
    if (isLoading) return;
    
    setIsLoading(true);
    try {
      // Extract parameters and retry with fallback enabled
      const { task_type, ...otherParams } = params;
      
      const { data, error } = await supabase.functions.invoke('flowith-neo', {
        body: {
          action: 'start',
          task_type,
          params: {
            ...otherParams,
            allow_fallback: true,  // Force fallback enabled
            prefer_provider: 'neo',
            retry_from_execution: executionId
          }
        }
      });

      if (error) throw error;

      if (data.success) {
        toast({
          title: "Task Restarted",
          description: `${task_type} collection restarted with fallback enabled`,
        });
        
        setTimeout(loadNeoExecutions, 2000);
      } else {
        throw new Error(data.error || 'Failed to restart task');
      }

    } catch (error) {
      console.error('Error restarting task:', error);
      toast({
        title: "Error",
        description: `Failed to restart task: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const rerunWithFallback = async (execution: NeoExecution) => {
    const taskType = execution.dataset;
    await handleRetryWithFallback(execution.run_id, { task_type: taskType });
  };

  const checkTaskStatus = async (execution: NeoExecution) => {
    if (!execution.run_id) return;
    
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('flowith-neo', {
        body: {
          action: 'status',
          run_id: execution.run_id
        }
      });

      if (error) throw error;

      toast({
        title: "Status Check",
        description: `Task ${execution.run_id}: ${data.status || 'Unknown status'}`,
      });

      // Refresh the executions list
      setTimeout(loadNeoExecutions, 1000);

    } catch (error) {
      console.error('Error checking task status:', error);
      toast({
        title: "Error",
        description: `Failed to check status: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const testDirectIngestion = async (dataset: string) => {
    setIsLoading(true);
    try {
      // Get webhook URL from app_settings
      const { data: settingsData, error: settingsError } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'FLOWITH_WEBHOOK_URL')
        .single();

      if (settingsError || !settingsData) {
        throw new Error('FLOWITH_WEBHOOK_URL not configured in app_settings');
      }

      const run_id = `smoke-${dataset}-${Date.now()}`;
      const event_id = `evt-${Date.now()}`;
      
      let payload;
      switch (dataset) {
        case 'social':
          payload = {
            version: "1",
            dataset: "social",
            run_id,
            event_id,
            rows: [
              { slug: "bitcoin", date: "2025-09-08", twitter_followers: 7000000, reddit_subscribers: 6000000, score: 85.5 }
            ]
          };
          break;
        case 'derivatives':
          payload = {
            version: "1",
            dataset: "derivatives", 
            run_id,
            event_id,
            rows: [
              { symbol: "BTCUSDT", ts: new Date().toISOString(), open_interest_usd: 15000000000, funding_rate: 0.0001 }
            ]
          };
          break;
        case 'whales':
          payload = {
            version: "1",
            dataset: "whales",
            run_id,
            event_id,
            rows: [
              { asset: "bitcoin", date: "2025-09-08", metric_value: 125000, zscore: 2.1, signal: "accumulation" }
            ]
          };
          break;
        default:
          throw new Error(`Unsupported dataset: ${dataset}`);
      }

      const response = await fetch(settingsData.value, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      toast({
        title: `✅ ${dataset.toUpperCase()} Test OK`,
        description: `rows_inserted: ${result.rows_inserted || 0}`,
      });
      
      // Wait a moment then refresh executions
      setTimeout(() => {
        loadNeoExecutions();
      }, 1000);

    } catch (error: any) {
      console.error(`Error testing ${dataset} ingestion:`, error);
      toast({
        title: `❌ ${dataset.toUpperCase()} Test Failed`,
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const callInternalCollector = async (taskType: string) => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      const collectorMap = {
        'social': 'collect-social-history',
        'derivatives': 'collect-derivatives-history', 
        'whales': 'collect-whales-history'
      };

      const functionName = collectorMap[taskType];
      if (!functionName) {
        throw new Error(`No internal collector for ${taskType}`);
      }

      const { data, error } = await supabase.functions.invoke(functionName, {
        body: { 
          days: 1,
          symbols: taskType === 'derivatives' ? ['BTC', 'ETH'] : undefined,
          slugs: taskType === 'social' ? ['bitcoin', 'ethereum'] : undefined
        }
      });

      if (error) throw error;

      toast({
        title: "✅ Internal Collection Complete",
        description: `${TASK_TYPES.find(t => t.value === taskType)?.label} - ${data.rows || 0} rows collected`,
      });
      
      setTimeout(loadNeoExecutions, 2000);

    } catch (error) {
      console.error('Error in internal collection:', error);
      toast({
        title: "❌ Internal Collection Failed",
        description: `${taskType}: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIcon = (status: string, startedAt?: string) => {
    const isStalled = status === 'running' && startedAt && 
      (Date.now() - new Date(startedAt).getTime()) > 5 * 60 * 1000; // 5 minutes

    switch (status) {
      case 'running':
        return isStalled ? 
          <AlertTriangle className="h-4 w-4 text-orange-500" /> :
          <RefreshCw className="h-4 w-4 animate-spin" />;
      case 'pending':
        return <RefreshCw className="h-4 w-4 animate-spin" />;
      case 'queued':
        return <Clock className="h-4 w-4 text-orange-500" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusBadgeVariant = (status: string, startedAt?: string) => {
    const isStalled = status === 'running' && startedAt && 
      (Date.now() - new Date(startedAt).getTime()) > 5 * 60 * 1000; // 5 minutes

    switch (status) {
      case 'completed':
        return 'default';
      case 'running':
        return isStalled ? 'destructive' : 'secondary';
      case 'pending':
        return 'secondary';
      case 'queued':
        return 'outline';
      case 'failed':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const formatExecutionTime = (startedAt: string, completedAt?: string) => {
    const start = new Date(startedAt);
    const end = completedAt ? new Date(completedAt) : new Date();
    const diffMs = end.getTime() - start.getTime();
    
    if (diffMs < 60000) {
      return `${Math.floor(diffMs / 1000)}s`;
    } else if (diffMs < 3600000) {
      return `${Math.floor(diffMs / 60000)}m ${Math.floor((diffMs % 60000) / 1000)}s`;
    } else {
      const hours = Math.floor(diffMs / 3600000);
      const minutes = Math.floor((diffMs % 3600000) / 60000);
      return `${hours}h ${minutes}m`;
    }
  };

  const isExecutionStalled = (status: string, startedAt: string) => {
    return status === 'running' && (Date.now() - new Date(startedAt).getTime()) > 5 * 60 * 1000;
  };

  const getTaskLabel = (dataset: string) => {
    // Map the dataset (tool_name) to readable labels
    const taskMap: { [key: string]: string } = {
      'flowith_neo_collect': 'Neo Data Collection',
      'social': 'Social History',
      'derivatives': 'Derivatives',
      'whales': 'Whales Activity',
      'fng': 'Fear & Greed Index'
    };
    
    return taskMap[dataset] || dataset;
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5" />
            Agent Neo Control Panel
          </CardTitle>
          <CardDescription>
            Manage intelligent data collection tasks using Flowith's Neo agent
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          
          {/* Quick Actions - Hybrid Mode (Default) */}
          <div>
            <h3 className="text-sm font-medium mb-3">Quick Update (Last 24h) - Hybrid Mode</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {TASK_TYPES.map((task) => (
                <Button
                  key={task.value}
                  variant="outline"
                  size="sm"
                  onClick={() => startNeoTask(task.value, 1, 'neo', true)}
                  disabled={isLoading}
                  className="flex items-center gap-2"
                >
                  <Play className="h-3 w-3" />
                  {task.label}
                </Button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Prefer Neo provider with internal fallback. Robust data collection.
            </p>
          </div>

          {/* Neo Only (No Fallback) */}
          <div>
            <h3 className="text-sm font-medium mb-3">Neo Only (No Fallback)</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {TASK_TYPES.map((task) => (
                <Button
                  key={`neo-${task.value}`}
                  variant="outline"
                  size="sm"
                  onClick={() => startNeoTask(task.value, 1, 'neo', false)}
                  disabled={isLoading}
                  className="flex items-center gap-2 border-blue-500 text-blue-600 hover:bg-blue-50"
                >
                  <Bot className="h-3 w-3" />
                  {task.label}
                </Button>
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => startNeoTask('social', 1, 'neo', false, ['bitcoin'])}
              disabled={isLoading}
              className="flex items-center gap-2 border-orange-500 text-orange-600 hover:bg-orange-50 mt-2"
            >
              <Bot className="h-3 w-3" />
              🧪 Neo Smoke (Bitcoin Only)
            </Button>
            <p className="text-xs text-muted-foreground mt-2">
              Forces Neo provider usage - no internal fallback. Smoke test uses Bitcoin only.
            </p>
          </div>

          {/* Direct Ingestion Tests */}
          <div>
            <h3 className="text-sm font-medium mb-3">Direct Ingestion Tests</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {TASK_TYPES.filter(task => ['social', 'derivatives', 'whales'].includes(task.value)).map((task) => (
                <Button
                  key={`test-${task.value}`}
                  variant="secondary"
                  size="sm"
                  onClick={() => testDirectIngestion(task.value)}
                  disabled={isLoading}
                  className="flex items-center gap-2"
                >
                  🧪 Test {task.label}
                </Button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Direct POST to webhook with sample data - validates ingestion pipeline
            </p>
          </div>

          {/* Internal Fallback Collection */}
          <div>
            <h3 className="text-sm font-medium mb-3">Internal Fallback Collection</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {TASK_TYPES.filter(task => ['social', 'derivatives', 'whales'].includes(task.value)).map((task) => (
                <Button
                  key={`internal-${task.value}`}
                  variant="outline"
                  size="sm"
                  onClick={() => callInternalCollector(task.value)}
                  disabled={isLoading}
                  className="flex items-center gap-2"
                >
                  ⚡ {task.label}
                </Button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Direct internal collection (bypasses external providers)
            </p>
          </div>

          {/* Backfill Operations */}
          <div>
            <h3 className="text-sm font-medium mb-3">Backfill Operations</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {TASK_TYPES.map((task) => (
                <div key={task.value} className="space-y-2">
                  <div className="text-sm font-medium">{task.label}</div>
                  <div className="text-xs text-muted-foreground mb-2">{task.description}</div>
                  <div className="flex gap-1">
                    {BACKFILL_PERIODS.map((period) => (
                      <Button
                        key={period.value}
                        variant="ghost"
                        size="sm"
                        onClick={() => startNeoTask(task.value, period.value)}
                        disabled={isLoading}
                        className="text-xs"
                      >
                        {period.label}
                      </Button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

        </CardContent>
      </Card>

      {/* Execution Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Recent Executions</span>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={async () => {
                  try {
                    setIsLoading(true);
                    
                    // Get webhook URL from app_settings
                    const { data: webhookSetting } = await supabase
                      .from('app_settings')
                      .select('value')
                      .eq('key', 'FLOWITH_WEBHOOK_URL')
                      .single();

                    if (!webhookSetting?.value) {
                      throw new Error('FLOWITH_WEBHOOK_URL not configured in app_settings');
                    }

                    // Create test payload
                    const testPayload = {
                      version: "1",
                      dataset: "fng",
                      run_id: "smoke-" + Date.now(),
                      event_id: "evt-" + Date.now(),
                      rows: [
                        { date: "2025-09-08", value: 55, classification: "Neutral" }
                      ]
                    };

                    // POST directly to webhook
                    const response = await fetch(webhookSetting.value, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json'
                      },
                      body: JSON.stringify(testPayload)
                    });

                    if (!response.ok) {
                      const errorText = await response.text();
                      throw new Error(`${response.status}: ${errorText}`);
                    }

                    const result = await response.json();
                    
                    if (result.success) {
                      toast({
                        title: "✅ Smoke Test OK",
                        description: `rows_inserted: ${result.rows_inserted}`,
                      });
                      
                      // Refresh executions after 1 second
                      setTimeout(() => {
                        loadNeoExecutions();
                      }, 1000);
                    } else {
                      toast({
                        title: "❌ Smoke Test Failed", 
                        description: result.error,
                        variant: "destructive"
                      });
                    }
                  } catch (error) {
                    console.error('Smoke test error:', error);
                    toast({
                      title: "❌ Test Error",
                      description: error instanceof Error ? error.message : "Failed to run smoke test",
                      variant: "destructive"
                    });
                  } finally {
                    setIsLoading(false);
                  }
                }}
                disabled={isLoading}
                title="Test Neo webhook integration end-to-end"
              >
                🧪 Test
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={loadNeoExecutions}
                disabled={isLoading}
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </CardTitle>
          <CardDescription>
            Real-time status of Neo data collection tasks
          </CardDescription>
        </CardHeader>
        <CardContent>
          {executions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No Neo tasks executed yet. Start your first collection above.
            </div>
          ) : (
            <div className="space-y-3">
              {executions.map((execution) => (
                <div
                  key={execution.run_id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div className="flex items-center gap-3 flex-1">
                    {getStatusIcon(execution.status, execution.created_at)}
                    <div className="flex-1">
                      <div className="font-medium text-sm flex items-center gap-2">
                        {getTaskLabel(execution.dataset)}
                        {execution.provider === 'neo' && (
                          <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700">
                            Neo
                          </Badge>
                        )}
                        {execution.fallback_used && (
                          <Badge variant="outline" className="text-xs">
                            Fallback
                          </Badge>
                        )}
                        {isExecutionStalled(execution.status, execution.created_at) && (
                          <Badge variant="destructive" className="text-xs">
                            Possível travamento
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Tempo: {formatExecutionTime(execution.created_at, execution.status === 'completed' ? execution.created_at : undefined)}
                        {execution.rows_inserted && (
                          <span className="ml-4">Linhas: {execution.rows_inserted}</span>
                        )}
                        {execution.provider && (
                          <span className="ml-4">Provedor: {execution.provider}</span>
                        )}
                      </div>
                      
                      {/* Enhanced Error Display */}
                      {execution.status === 'queued' && (
                        <div className="text-xs text-orange-600 mt-1 flex items-center gap-2">
                          <Clock className="h-3 w-3" />
                          Queued for retry - Neo service temporarily unavailable
                        </div>
                      )}
                      
                      {execution.status === 'failed' && execution.result?.metrics?.last_error && (
                        <div className="text-xs text-red-600 mt-1 flex items-center gap-2">
                          <XCircle className="h-3 w-3" />
                          {execution.result.metrics.last_error}
                        </div>
                      )}
                      
                      {execution.error_message && !execution.result?.metrics?.last_error && (
                        <div className="text-xs text-red-600 mt-1">
                          <XCircle className="h-3 w-3 inline mr-1" />
                          {execution.error_message}
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex gap-2 mt-2">
                        {(execution.status === 'failed' || execution.status === 'queued') && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => rerunWithFallback(execution)}
                            disabled={isLoading}
                          >
                            <RefreshCw className="h-4 w-4 mr-2" />
                            {execution.status === 'queued' ? 'Forçar fallback agora' : 'Tentar com Fallback'}
                          </Button>
                        )}
                        {execution.status === 'running' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => checkTaskStatus(execution)}
                            disabled={isLoading}
                          >
                            <Search className="h-4 w-4 mr-2" />
                            Reconsultar status
                          </Button>
                        )}
                        {isExecutionStalled(execution.status, execution.created_at) && (
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => rerunWithFallback(execution)}
                            disabled={isLoading}
                          >
                            <AlertTriangle className="h-4 w-4 mr-2" />
                            Forçar fallback agora
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    {execution.rows_inserted > 0 && (
                      <div className="text-sm text-green-600" title="Rows inserted (idempotent upserts)">
                        +{execution.rows_inserted} inserted
                        {execution.rows_updated > 0 && ` / ${execution.rows_updated} updated`}
                      </div>
                    )}
                    
                    <div className="flex flex-col items-end gap-1">
                      <Badge variant={getStatusBadgeVariant(execution.status, execution.created_at)}>
                        {execution.status}
                        {isExecutionStalled(execution.status, execution.created_at) && " (Travado)"}
                      </Badge>
                      <div className="flex gap-1">
                        <a 
                          href={`https://supabase.com/dashboard/project/pafnmppyisnhfqfozjag/functions/flowith-neo/logs`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:underline"
                        >
                          Neo logs
                        </a>
                        {execution.provider === 'neo' && (
                          <a 
                            href={`https://supabase.com/dashboard/project/pafnmppyisnhfqfozjag/functions/flowith-neo-webhook/logs`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline"
                          >
                            Webhook logs
                          </a>
                        )}
                        <a 
                          href={`https://supabase.com/dashboard/project/pafnmppyisnhfqfozjag/sql/new`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:underline"
                        >
                          SQL view
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}